import { CONSUMER_BANKING_TEAM_CODES, GIT_RELEASE_BRANCH } from '@/config'
import { getRouter, paths } from '@/router'
import ApiService from '@/service'
import type {
  CreateRuleInput,
  PolicyTest,
  RequestTemplate,
  RuleTest,
  RuleTestResult,
  UsedKeys,
  UsedValues,
} from '@/types'
import { LATEST_SUPPORTED_RULE_FORMAT_VERSION } from '@/utils/constants'
import { generateId } from '@/utils/generateId'
import notifications from '@/utils/notifications'
import * as testValueUtils from '@/utils/testValueUtils'
import { alertUser, deepClone, draftBranchName } from '@/utils/utils'
import { moduleActionContext } from '@/vuex'
import type {
  CreateRuleRequest,
  Policy,
  PullRequest,
  Rule,
  TestResult,
} from '@klarna/cbrms-policy-rules-manager-client-ts/api'
import axios from 'axios'
import { defineActions, defineGetters, defineModule, defineMutations } from 'direct-vuex'
import indexOf from 'lodash/indexOf'
import intersection from 'lodash/intersection'
import keys from 'lodash/keys'
import pick from 'lodash/pick'
import sortBy from 'lodash/sortBy'
import { findInvalidInputsAndHelpers, migrateRule, removeInputAndHelperReferences } from '../utils/migrations'
import { AuthState } from './authModule'

const restoreKey = (id: string, branch: string): string => {
  return `${id}/${branch}`
}

let pollingTimer: number | undefined = undefined

export const ruleActionContext = (context: any) => moduleActionContext(context, ruleModule)

export interface RuleState {
  isEditingTestName: boolean
  // TODO: should initially be undefined
  currentRule: Rule
  pristineCurrentRule: Rule
  currentBranch: string
  currentPr?: PullRequest
  insertedFunction?: {
    functionName: string
    usageExample: string
  }
  insertedVariable?: string
  isLoading: boolean
  isSaving: boolean
  currentRuleBranches: string[]
  usedValues: UsedValues
  consoleResult?: RuleTestResult
  functionToHighlight?: string
  inputToHighlight?: string
  isDraftOutdated?: boolean
  pollingTimer?: number
  isPRMerging: boolean
  isPRLoading: boolean
}

const ruleModule = defineModule({
  namespaced: true,

  state: {
    isEditingTestName: false,
    // TODO: initial value should be undefined, but will required extensive rework of components
    currentRule: {} as Rule,
    pristineCurrentRule: {} as Rule,
    currentBranch: GIT_RELEASE_BRANCH,
    currentRuleBranches: [],
    currentPr: undefined,
    insertedFunction: undefined,
    insertedVariable: undefined,
    isLoading: false,
    isSaving: false,
    rulesLoading: false,
    usedValues: { inputs: {}, helpers: {} },
    consoleResult: undefined,
    functionToHighlight: undefined,
    inputToHighlight: undefined,
    isDraftOutdated: false,
    pollingTimer: undefined,
    isPRMerging: false,
    isPRLoading: false,
  } as RuleState,

  getters: defineGetters<RuleState>()({
    isRelease(state: RuleState) {
      return state.currentBranch === GIT_RELEASE_BRANCH
    },
    isMyBranch(state: RuleState, getters: any, rootState: any) {
      const auth = rootState.auth as AuthState
      const username = auth.user && auth.user.username
      return username === state.currentBranch
    },
    myBranchExists(state: RuleState, getters: any, rootState: any) {
      const auth = rootState.auth as AuthState
      const username = auth.user && auth.user.username
      return username && state.currentRuleBranches.includes(username)
    },
    isRuleUsedInPolicy(state: RuleState) {
      const policies = state.currentRule.policies
      return policies && policies.length > 0
    },
    currentRuleHasChanges(state: RuleState) {
      const { currentRule, pristineCurrentRule } = state
      const mutableFields = ['content', 'labels', 'description', 'unittests', 'jira_issue']
      return (
        JSON.stringify(pick(currentRule, ...mutableFields)) !==
        JSON.stringify(pick(pristineCurrentRule, ...mutableFields))
      )
    },
    currentRuleUsedKeys(state: RuleState): UsedKeys {
      return {
        inputs: sortBy(state.currentRule.content_inputs) || [],
        helpers: sortBy(state.currentRule.content_helpers) || [],
      }
    },

    deprecatedRuleKeys(state: RuleState, getters: any, rootState: any, rootGetters: any) {
      const deprecatedHelpers = intersection(
        getters.currentRuleUsedKeys.helpers,
        rootGetters['scriptKey/deprecatedKeys'].deprecatedHelpers
      )
      const deprecatedInputs = intersection(
        getters.currentRuleUsedKeys.inputs,
        rootGetters['scriptKey/deprecatedKeys'].deprecatedInputs
      )

      return [...deprecatedHelpers, ...deprecatedInputs]
    },

    isOwningOrAdminTeam(state: RuleState, getters: any, rootState: any, rootGetters: any) {
      const auth = rootState.auth as AuthState
      const ruleTeamCode = state.currentRule.team_code
      const teamCode = auth.userInfo && auth.userInfo.team_code
      const additionalTeams = auth.userInfo && auth.userInfo.additional_teams

      // Iris team has admin rights
      if (rootGetters['auth/isAdminTeam']) {
        return true
      }

      if (!ruleTeamCode) return false

      if (ruleTeamCode === teamCode) {
        return true
        // Check if team code belongs to a team group
      } else if (additionalTeams && additionalTeams.length) {
        const teamGroup = additionalTeams.map(el => el.team_code)
        return teamGroup.includes(ruleTeamCode)
      } else {
        // Migration of team code after the split of the Consumer Banking Support team
        if (CONSUMER_BANKING_TEAM_CODES.includes(ruleTeamCode)) {
          return teamCode && CONSUMER_BANKING_TEAM_CODES.includes(teamCode)
        }
      }
    },

    isRuleUsed(state: RuleState) {
      return state.currentRule.policies && state.currentRule.policies.length > 0
    },

    ruleHasNoDrafts(state: RuleState) {
      return state.currentRuleBranches && state.currentRuleBranches.length === 1
    },
  }),

  mutations: defineMutations<RuleState>()({
    // This is for unit test usage, so one can setup test state without being mega-intrusive
    REPLACE(state: RuleState, payload: Partial<RuleState>) {
      const keys = Object.keys(payload) as (keyof RuleState)[]

      keys.forEach((key: keyof RuleState) => {
        state[key] = payload[key] as never
      })
    },

    SET_EDITING_TEST_NAME(state: RuleState, isEditingTestName: boolean) {
      state.isEditingTestName = isEditingTestName
    },

    SET_CONSOLE_RESULT(state: RuleState, payload?: RuleTestResult) {
      state.consoleResult = payload
    },

    SET_RULE_LOADING(state: RuleState, loading: boolean) {
      state.isLoading = loading
    },

    SET_RULE_SAVING(state: RuleState, saving: boolean) {
      state.isSaving = saving
    },

    SET_CURRENT_RULE(state: RuleState, { rule }: { rule: Rule }) {
      // Make sure we have a "unittests" property so it's reactive
      if (!rule.unittests) {
        rule = { ...rule, unittests: [] }
      }
      state.currentRule = rule
    },

    SET_CURRENT_RULE_SCRIPT(state: RuleState, { script }: { script: string }) {
      state.currentRule = {
        ...state.currentRule,
        content: script,
      }
    },

    SET_PRISTINE_CURRENT_RULE(state: RuleState, rule: Rule) {
      state.pristineCurrentRule = rule
    },

    UPDATE_CURRENT_RULE(state: RuleState, payload: Partial<Rule>) {
      state.currentRule = { ...state.currentRule, ...payload }
    },

    SET_INSERT_FUNCTION(
      state: RuleState,
      payload?: {
        functionName: string
        usageExample: string
        functionType?: string
      }
    ) {
      state.insertedFunction = payload
    },

    SET_INSERT_VARIABLE(state: RuleState, payload?: string) {
      state.insertedVariable = payload
    },

    SET_RULE_TESTS(state: RuleState, payload: RuleTest[]) {
      state.currentRule.unittests = payload
    },

    REORDER_RULE_TESTS(state: RuleState, payload: { fromIndex: number; toIndex: number }) {
      const { fromIndex, toIndex } = payload
      if (!state.currentRule.unittests) return

      const test = state.currentRule.unittests[fromIndex]
      state.currentRule.unittests.splice(fromIndex, 1)
      state.currentRule.unittests.splice(toIndex, 0, test)
    },

    UPDATE_RULE_TEST(state: RuleState, { index, test }: { index: number; test: RuleTest }) {
      const { unittests = [] } = state.currentRule
      const newUnitTests = [...unittests]
      newUnitTests[index] = test
      state.currentRule.unittests = newUnitTests
    },
    SET_USED_KEYS(state: RuleState, { inputs, helpers }: UsedKeys) {
      state.currentRule.content_inputs = inputs
      state.currentRule.content_helpers = helpers
    },
    SET_RULE_PULL_REQUEST(state: RuleState, pr: PullRequest | undefined) {
      state.currentPr = pr
    },
    DELETED_RULE_PULL_REQUEST(state: RuleState) {
      state.currentPr = undefined
    },
    SET_CURRENT_RULE_BRANCHES(state: RuleState, payload: string[]) {
      state.currentRuleBranches = payload
    },
    SET_CURRENT_RULE_BRANCH(state: RuleState, branch: string) {
      state.currentBranch = branch
    },

    SET_RULE_RUN_VALUES(state: RuleState, payload: UsedValues) {
      state.usedValues = payload
    },

    SET_HIGHLIGHTED_FUNCTION(state: RuleState, name?: string) {
      state.functionToHighlight = name
    },

    SET_HIGHLIGHTED_INPUT(state: RuleState, name?: string) {
      state.inputToHighlight = name
    },

    SET_DRAFT_IS_OUTDATED(state: RuleState, status: boolean) {
      state.isDraftOutdated = status
    },

    SET_POLLING_TIMER(state: RuleState, timer: number | undefined) {
      state.pollingTimer = timer
    },

    SET_IS_PR_MERGING(state: RuleState, merging: boolean) {
      state.isPRMerging = merging
    },

    SET_IS_PR_LOADING(state: RuleState, loading: boolean) {
      state.isPRLoading = loading
    },
  }),

  actions: defineActions({
    async OPEN_RULE_PR(ctx, payload: { description: string }): Promise<PullRequest> {
      const { state, commit, dispatch } = ruleActionContext(ctx)
      const {
        currentRule: { id },
        currentBranch,
      } = state

      commit.SET_RULE_LOADING(true)

      await dispatch.SAVE_CURRENT_RULE({
        operationType: 'SAVE_BEFORE_PR',
      })

      try {
        const { data: pullRequest } = await ApiService.openRulePullRequest(id, currentBranch, payload)

        commit.SET_RULE_PULL_REQUEST(pullRequest)
        return pullRequest
      } finally {
        commit.SET_RULE_LOADING(false)
        dispatch.POLL_RULE_PR_STATUS({ ruleId: id, branch: currentBranch })
      }
    },

    async DELETE_RULE_PR(ctx): Promise<void> {
      const { state, commit } = ruleActionContext(ctx)
      const {
        currentRule: { id },
        currentBranch,
      } = state

      commit.SET_RULE_LOADING(true)

      await ApiService.deleteRulePullRequest(id, currentBranch)

      commit.DELETED_RULE_PULL_REQUEST()
      commit.SET_RULE_LOADING(false)
    },

    async GET_RULE_PR(ctx, { ruleId, branch }: { ruleId: string; branch: string }): Promise<PullRequest | undefined> {
      const { commit, dispatch } = ruleActionContext(ctx)

      try {
        commit.SET_IS_PR_LOADING(true)
        const { data: pullRequest } = await ApiService.getRulePullRequest(ruleId, draftBranchName(branch))

        if (pullRequest.state === 'OPEN') {
          dispatch.POLL_RULE_PR_STATUS({ ruleId, branch })
        }
        commit.SET_RULE_PULL_REQUEST(pullRequest)

        return pullRequest
      } finally {
        commit.SET_IS_PR_LOADING(false)
      }
    },

    POLL_RULE_PR_STATUS(ctx, { ruleId, branch }: { ruleId: string; branch: string }): Promise<string> {
      const { commit, dispatch, state, rootDispatch } = ruleActionContext(ctx)
      const { currentBranch } = state

      return new Promise(resolve => {
        return dispatch
          .GET_CURRENT_RULE_BRANCHES(ruleId)
          .then(async res => {
            if (!res.includes(currentBranch)) {
              commit.SET_RULE_PULL_REQUEST(undefined)
              getRouter()?.push(paths.ruleBuilder.replace(':rule_id', ruleId).replace(':branch?', GIT_RELEASE_BRANCH))

              commit.SET_IS_PR_MERGING(false)
              rootDispatch.SHOW_NOTIFICATION({
                message: notifications.successfullyMerged(ruleId),
              })
              return resolve(status)
            }

            // Check if PR has been approved
            const { data: pullRequest } = await ApiService.getRulePullRequest(ruleId, draftBranchName(branch))
            if (pullRequest.state === 'OPEN') commit.SET_RULE_PULL_REQUEST(pullRequest)

            // uncomment it to enable poll mechanism (CBRMS-2147)
            //setTimeout(
            //  () => dispatch.POLL_RULE_PR_STATUS({ ruleId, branch }),
            //  10000
            //);
          })
          .catch(() => undefined)
      })
    },

    async MERGE_PULL_REQUEST(ctx): Promise<void> {
      const { commit, state, dispatch } = ruleActionContext(ctx)
      const { currentRule } = state

      if (!state.currentPr) return

      commit.SET_IS_PR_MERGING(true)
      try {
        await ApiService.mergeRulePullRequest(currentRule.id, state.currentBranch, {
          id: state.currentPr.id,
          version: state.currentPr.version,
        })

        dispatch.POLL_RULE_PR_STATUS({
          ruleId: currentRule.id,
          branch: state.currentBranch,
        })
      } catch (e) {
        commit.SET_IS_PR_MERGING(false)
        throw new Error(
          `We couldn't merge your Pull Request.
          Please <strong>make sure all required checks are passing</strong>, then try again.<br><br>
          If the issue persists, please <strong>contact us</strong>.`
        )
      }
    },

    async CREATE_RULE(ctx, rule: CreateRuleInput): Promise<Rule> {
      const {
        commit,
        rootState: { scriptKey },
        rootDispatch,
      } = ruleActionContext(ctx)
      commit.SET_RULE_LOADING(true)
      const newRule: CreateRuleRequest = {
        id: rule.id,
        api: rule.api,
        jira_issue: rule.jira_issue,
        format_version: rule.format_version || LATEST_SUPPORTED_RULE_FORMAT_VERSION,
        description: rule.description,
        labels: rule.labels || [],
        unittests: rule.unittests || [],
        content_inputs: rule.content_inputs || [],
        content_helpers: rule.content_helpers || [],
      }
      try {
        const { data: savedRule } = await ApiService.createRule(newRule)
        // Ensure a draft branch exists
        const { data: ruleDraft } = await ApiService.updateRule(rule.id, {
          ...savedRule,
          content: rule.content ? rule.content : savedRule.content,
          unittests: rule.unittests ? rule.unittests : [],
          commit_message: 'create draft',
        })

        if (!scriptKey.inputs) {
          await rootDispatch.scriptKey.GET_INPUTS_METADATA()
        }

        if (!scriptKey.helpers) {
          await rootDispatch.scriptKey.GET_HELPERS_METADATA()
        }

        return ruleDraft
      } finally {
        commit.SET_RULE_LOADING(false)
      }
    },

    // eslint-disable-next-line no-unused-vars
    async GET_RULE(ctx, { ruleId, branch }: { ruleId: string; branch?: string }): Promise<Rule> {
      const { data: rule } = await ApiService.getRule(ruleId, branch || GIT_RELEASE_BRANCH)
      return rule
    },

    async GET_CURRENT_RULE(ctx, { ruleId, branch }: { ruleId: string; branch?: string }): Promise<Rule> {
      const { commit, dispatch, rootDispatch, rootState, rootGetters } = ruleActionContext(ctx)
      commit.SET_RULE_LOADING(true)
      // Reset anything related to previously loaded rule
      commit.SET_CURRENT_RULE({ rule: {} as Rule })
      commit.SET_RULE_RUN_VALUES({ inputs: {}, helpers: {} })
      commit.SET_CONSOLE_RESULT(undefined)
      commit.SET_EDITING_TEST_NAME(false)
      commit.SET_RULE_PULL_REQUEST(undefined)
      commit.SET_DRAFT_IS_OUTDATED(false)

      const branchesPromise = dispatch.GET_CURRENT_RULE_BRANCHES(ruleId)
      if (!branch) {
        const branches = await branchesPromise
        branch = branches.includes(rootState.auth.user!.username) ? rootState.auth.user!.username : GIT_RELEASE_BRANCH
      }

      commit.SET_CURRENT_RULE_BRANCH(branch!)

      try {
        const { data: originalRule } = await ApiService.getRule(ruleId, branch!)

        const { invalidInputs, invalidHelpers, isCorrupt } = findInvalidInputsAndHelpers(
          rootGetters.scriptKey.scriptKeysLookup,
          originalRule
        )

        if (isCorrupt) {
          let msg = 'Rule contained legacy references to removed inputs or helpers.'
          if (invalidInputs.length > 0) {
            msg += `\nRemoving input references: ${invalidInputs.join(', ')}`
          }

          if (invalidHelpers.length > 0) {
            msg += `\nRemoving helper function references: ${invalidHelpers.join(', ')}`
          }

          msg += '\nNote the script may still refer to them, please correct it.'

          rootDispatch.SHOW_NOTIFICATION({ message: msg })
        }

        const fixedRule = isCorrupt
          ? removeInputAndHelperReferences({ inputs: invalidInputs, helpers: invalidHelpers }, originalRule)
          : originalRule

        const rule = migrateRule(rootGetters.scriptKey.scriptKeysLookup, fixedRule)

        if (rule.format_version > LATEST_SUPPORTED_RULE_FORMAT_VERSION) {
          alertUser(
            'The loaded rule may not be compatible with your client. Please refresh and reload at your earliest convenience.'
          )
        }

        const storedRule: string | null = window.localStorage.getItem(restoreKey(ruleId, branch!))

        if (storedRule && JSON.parse(storedRule).format_version === LATEST_SUPPORTED_RULE_FORMAT_VERSION) {
          const useStoredRule = window.confirm('Looks like you have an unsaved of this rule locally, restore it?')
          if (useStoredRule) {
            const currentRule = JSON.parse(storedRule)
            commit.SET_CURRENT_RULE({
              rule: currentRule,
            })
          } else {
            commit.SET_CURRENT_RULE({ rule })
            window.localStorage.removeItem(restoreKey(ruleId, branch!))
          }
        } else {
          commit.SET_CURRENT_RULE({ rule })
        }

        commit.SET_PRISTINE_CURRENT_RULE(deepClone(rule))
        return rule
      } catch (err) {
        if (axios.isAxiosError(err) && err && err.response && err.response.status === 404) {
          // Mute error. We'll give a more subtle error to user
          // eslint-disable-next-line prettier/prettier
          ;(err as any).silent = true
        }
        throw err
      } finally {
        commit.SET_RULE_LOADING(false)
      }
    },

    async SAVE_CURRENT_RULE(
      ctx,
      args?: {
        commitMessage?: string
        operationType: 'SAVE_SILENTLY' | 'SAVE' | 'SAVE_BEFORE_PR' | 'CREATE_NEW_BRANCH'
      }
    ): Promise<Rule> {
      const { commitMessage = 'save', operationType = 'SAVE_SILENTLY' } = args || {}

      const { commit, state, rootDispatch, rootState, rootGetters, getters } = ruleActionContext(ctx)

      if (['SAVE', 'SAVE_SILENTLY', 'SAVE_BEFORE_PR'].includes(operationType)) {
        const hasChange = getters.currentRuleHasChanges

        if (!hasChange && operationType === 'SAVE') {
          rootDispatch.SHOW_NOTIFICATION({
            message: notifications.skipSave,
            state: 'info',
          })
        }

        if (!hasChange) {
          return state.currentRule
        }
      }

      const { currentRule } = state
      const { username } = rootState.auth.user!

      const ruleCommit = {
        ...currentRule,
        unittests: testValueUtils.sanitizeTests(
          rootGetters.scriptKey.scriptKeysLookup,
          currentRule.unittests || [],
          getters.currentRuleUsedKeys
        ),
        commit_message: commitMessage,
      }

      commit.SET_RULE_LOADING(true)
      commit.SET_RULE_SAVING(true)

      try {
        const { data: newRule } = await ApiService.updateRule(currentRule.id, ruleCommit)

        window.localStorage.removeItem(restoreKey(currentRule.id, currentRule.branch_name))

        const updatedRule = {
          // TODO: we only get partial rule information after the PUT request
          ...state.currentRule,
          ...newRule,
        }
        commit.SET_CURRENT_RULE_BRANCH(username)

        commit.SET_CURRENT_RULE({
          rule: {
            ...updatedRule,
          },
        })
        commit.SET_PRISTINE_CURRENT_RULE(deepClone(updatedRule))

        if (operationType !== 'SAVE_SILENTLY') {
          rootDispatch.SHOW_NOTIFICATION({
            message: {
              SAVE: notifications.successfullySave,
              SAVE_BEFORE_PR: notifications.successfullySave,
              CREATE_NEW_BRANCH: notifications.successfullyCreateDraft,
            }[operationType],
          })
        }

        return updatedRule
      } finally {
        commit.SET_RULE_LOADING(false)
        commit.SET_RULE_SAVING(false)
      }
    },

    async ADD_CURRENT_RULE_TO_POLICY(ctx, policy: Policy): Promise<Policy> {
      const { commit, state, rootDispatch, rootState } = ruleActionContext(ctx)
      const rule = state.currentRule
      commit.SET_RULE_LOADING(true)

      let latestPolicy: Policy
      try {
        const { data } = await ApiService.getPolicy(policy.id, draftBranchName(rootState.auth.user!.username))
        latestPolicy = data
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          const { data } = await ApiService.getPolicy(policy.id, GIT_RELEASE_BRANCH)
          latestPolicy = data
        } else {
          throw e
        }
      }

      latestPolicy.rules.push(rule)
      latestPolicy.content_helpers = Array.from(
        new Set([...latestPolicy.content_helpers, ...(rule.content_helpers || [])])
      )
      latestPolicy.content_inputs = Array.from(
        new Set([...latestPolicy.content_helpers, ...(rule.content_inputs || [])])
      )
      await ApiService.updatePolicy(latestPolicy.id, {
        ...latestPolicy,
        commit_message: 'Commit',
      })

      commit.SET_RULE_LOADING(false)

      rootDispatch.SHOW_NOTIFICATION({
        message: notifications.successfullyAddRule,
      })

      return latestPolicy
    },

    ADD_RULE_TEST(ctx, { newTest, index }: { newTest?: RuleTest | PolicyTest; index?: number }): void {
      const { commit, dispatch, state } = ruleActionContext(ctx)
      newTest = newTest || {
        id: generateId(),
        inputs: {},
        helpers: {},
        result: {
          decision: 'ACCEPTED',
          expectations: [],
        },
      }

      const unittests = [...state.currentRule.unittests!]
      const newIndex = index === undefined ? unittests.length : index + 1

      unittests.splice(newIndex, 0, newTest!)

      commit.SET_RULE_TESTS(unittests)
      dispatch.AUTOSAVE_IN_LOCALSTORAGE()
    },

    REMOVE_RULE_TEST(ctx, idxToRemove: number): void {
      const { commit, dispatch, state } = ruleActionContext(ctx)

      const unittests = state.currentRule.unittests || []
      commit.SET_RULE_TESTS(unittests.filter((_: RuleTest, idx: number) => idx !== idxToRemove))

      dispatch.AUTOSAVE_IN_LOCALSTORAGE()
    },

    MOVE_RULE_TEST(ctx, { fromIndex, toIndex }: { fromIndex: number; toIndex: number }): void {
      const { commit, state } = ruleActionContext(ctx)
      const unittests = [...state.currentRule.unittests!]

      unittests.splice(toIndex, 0, unittests.splice(fromIndex, 1)[0])
      commit.SET_RULE_TESTS(unittests)
    },

    async RUN_RULE_TESTS(ctx): Promise<TestResult[]> {
      const { state, getters, rootGetters } = ruleActionContext(ctx)
      const usedKeys = getters.currentRuleUsedKeys
      const unittests = state.currentRule.unittests || []
      const script = state.currentRule.content || ''

      const sanitizedUnitTests: RuleTest[] = testValueUtils.sanitizeTests(
        rootGetters.scriptKey.scriptKeysLookup,
        unittests,
        usedKeys
      )

      const { data } = await ApiService.getRuleTestResults({
        unittests: sanitizedUnitTests,
        script,
      })

      return data
    },

    async GET_CURRENT_RULE_BRANCHES(ctx, id: string): Promise<string[]> {
      const { commit } = ruleActionContext(ctx)
      const { data: result } = await ApiService.getRuleBranches(id)
      const branches = result.map((b: string) => b.replace('draft/', ''))
      commit.SET_CURRENT_RULE_BRANCHES(branches)
      return branches
    },

    async LOAD_INITIAL_RULE_RUN_VALUES(ctx, request: RequestTemplate): Promise<void> {
      const { commit, rootDispatch, rootState, rootGetters } = ruleActionContext(ctx)

      const initialHelpersValues = rootState.scriptKey.helpers || (await rootDispatch.scriptKey.GET_HELPERS_METADATA())

      const usedKeys = rootGetters.scriptAnalysis.usedKeys

      const commonInputs: string[] = intersection(keys(request.request_dict), usedKeys.inputs)

      const commonHelpers: string[] = intersection(keys(initialHelpersValues), usedKeys.helpers)

      const stringifyIfObject = (val: any) => (typeof val === 'object' ? JSON.stringify(val, null, 2) : val)

      const payload: UsedValues = {
        inputs: keys(request.request_dict)
          .filter(key => indexOf(commonInputs, key) !== -1)
          .reduce(
            (acc, key) => ({
              ...acc,
              [key]: stringifyIfObject(request.request_dict[key]),
            }),
            {}
          ),
        helpers: keys(initialHelpersValues)
          .filter(key => indexOf(commonHelpers, key) !== -1)
          .reduce(
            (acc, key) => ({
              ...acc,
              [key]: stringifyIfObject(initialHelpersValues[key].sample_value),
            }),
            {}
          ),
      }

      commit.SET_RULE_RUN_VALUES(payload)
    },

    async RUN_RULE(ctx) {
      const { commit, state, getters, rootGetters } = ruleActionContext(ctx)
      const { usedValues } = state

      const { inputs, helpers } = testValueUtils.sanitizeTest(
        rootGetters.scriptKey.scriptKeysLookup,
        usedValues,
        getters.currentRuleUsedKeys
      )

      const payload = {
        inputs,
        helpers,
        script: state.currentRule.content!,
      }
      commit.SET_CONSOLE_RESULT(undefined)
      const { data: result } = await ApiService.getCodeExecutionResult(payload)
      commit.SET_CONSOLE_RESULT(result)
    },

    async FORMAT_SCRIPT(ctx): Promise<void> {
      const { state, commit } = ruleActionContext(ctx)

      const { data: result } = await ApiService.formatScript({
        script: state.currentRule.content!,
      })

      commit.SET_CURRENT_RULE_SCRIPT(result as any)
    },

    async DELETE_CURRENT_DRAFT(ctx, { ruleId, branch }: { ruleId: string; branch: string }) {
      const { commit, rootDispatch } = ruleActionContext(ctx)
      commit.SET_RULE_LOADING(true)
      try {
        await ApiService.deleteRuleBranch(ruleId, branch)
        localStorage.removeItem(restoreKey(ruleId, branch))

        rootDispatch.SHOW_NOTIFICATION({
          message: notifications.successfullyDeleteDraft,
          state: 'info',
        })

        // Clear state immediately so we don't trigger an update event for
        // the recently deleted draft before redirecting users to the release branch
        commit.SET_CURRENT_RULE({ rule: {} as Rule })
        getRouter()?.push(paths.ruleBuilder.replace(':rule_id', ruleId).replace(':branch?', GIT_RELEASE_BRANCH))
      } finally {
        commit.SET_RULE_LOADING(false)
      }
    },

    async DELETE_RULE(ctx, ruleId: string) {
      const { commit, rootDispatch } = ruleActionContext(ctx)
      commit.SET_RULE_LOADING(true)
      try {
        await ApiService.deleteRule(ruleId)
        rootDispatch.SHOW_NOTIFICATION({
          message: notifications.successfullyDeleteRule,
          state: 'info',
        })
        getRouter()?.push(paths.home)
      } finally {
        commit.SET_RULE_LOADING(false)
      }
    },

    AUTOSAVE_IN_LOCALSTORAGE(ctx) {
      const { state, getters } = ruleActionContext(ctx)
      const key = restoreKey(state.currentRule.id, state.currentRule.branch_name)
      if (getters.currentRuleHasChanges) {
        window.localStorage.setItem(key, JSON.stringify(deepClone(state.currentRule)))
      } else {
        window.localStorage.removeItem(key)
      }
    },

    UPDATE_CURRENT_RULE(ctx, payload: Partial<Rule>) {
      const { commit, dispatch } = ruleActionContext(ctx)
      commit.UPDATE_CURRENT_RULE(payload)
      dispatch.AUTOSAVE_IN_LOCALSTORAGE()
    },

    UPDATE_RULE_TESTS(ctx, { index, test }: { index: number; test: RuleTest }) {
      const { commit, dispatch } = ruleActionContext(ctx)
      commit.UPDATE_RULE_TEST({ index, test })
      dispatch.AUTOSAVE_IN_LOCALSTORAGE()
    },

    async POLL_IS_CURRENT_DRAFT_OUTDATED(ctx, { ruleId, branch }: { ruleId: string; branch: string }) {
      const { dispatch, commit } = ruleActionContext(ctx)

      const res = await dispatch.GET_RULE({ ruleId, branch })
      const sourceCommit = res.source_commit
      const latestReleaseCommit = res.latest_release_commit

      if (sourceCommit && sourceCommit !== latestReleaseCommit) {
        commit.SET_DRAFT_IS_OUTDATED(true)
        return
      }

      dispatch.CLEAR_TIMEOUT()

      pollingTimer = window.setTimeout(() => dispatch.POLL_IS_CURRENT_DRAFT_OUTDATED({ ruleId, branch }), 60000)
    },

    CLEAR_TIMEOUT() {
      clearTimeout(pollingTimer)
    },
  }),
})

export default ruleModule
